import { useEffect, useRef, useState } from "react";
import type { StripePlanPrice, StripePricesResponse } from "../api/checkout";
import type { PaymentPlanOption } from "./types";
import { VIREO_PLANS } from "./flowData";

const ZERO_DECIMAL_CURRENCIES = new Set([
  "bif", "clp", "djf", "gnf", "jpy", "kmf", "krw", "mga",
  "pyg", "rwf", "ugx", "vnd", "vuv", "xaf", "xof", "xpf"
]);

const toMajorUnits = (unitAmount: number, currency: string): number => {
  const code = currency.toLowerCase();
  return ZERO_DECIMAL_CURRENCIES.has(code) ? unitAmount : unitAmount / 100;
};

const formatUnitAmount = (unitAmount: number, currency: string): string => {
  const amount = toMajorUnits(unitAmount, currency);
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: currency.toUpperCase(),
      minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
      maximumFractionDigits: 2
    }).format(amount);
  } catch {
    return `${currency.toUpperCase()} ${amount.toFixed(2)}`;
  }
};

const intervalToWeeks = (interval: string, intervalCount: number): number => {
  switch (interval) {
    case "day":
      return intervalCount / 7;
    case "week":
      return intervalCount;
    case "month":
      return intervalCount * 4.33;
    case "year":
      return intervalCount * 52;
    default:
      return intervalCount * 4.33;
  }
};

const intervalToLabel = (interval: string, intervalCount: number): string => {
  if (interval === "year") {
    return intervalCount === 1 ? "1 year" : `${intervalCount} years`;
  }
  if (interval === "month") {
    if (intervalCount === 1) return "1 month";
    if (intervalCount === 12) return "1 year";
    return `${intervalCount} months`;
  }
  if (interval === "week") {
    return intervalCount === 1 ? "1 week" : `${intervalCount} weeks`;
  }
  return `${intervalCount} ${interval}`;
};

const PLAN_SAVE_PERCENT: Record<string, number> = {
  monthly: 40,
  quarterly: 50,
  annual: 60
};

const MOST_POPULAR_PLAN = "quarterly";

const mapStripePriceToOption = (planId: string, price: StripePlanPrice): PaymentPlanOption => ({
  id: planId,
  label: intervalToLabel(price.interval, price.intervalCount),
  savePercent: PLAN_SAVE_PERCENT[planId] ?? 0,
  weeksInPeriod: intervalToWeeks(price.interval, price.intervalCount),
  totalPrice: formatUnitAmount(price.unitAmount, price.currency),
  currencyCode: price.currency.toUpperCase(),
  amount: toMajorUnits(price.unitAmount, price.currency),
  isMostPopular: planId === MOST_POPULAR_PLAN
});

const mapPricesResponseToPlans = (prices: StripePricesResponse): PaymentPlanOption[] => [
  mapStripePriceToOption("monthly", prices.monthly),
  mapStripePriceToOption("quarterly", prices.quarterly),
  mapStripePriceToOption("annual", prices.annual)
];

type LivePricesState = {
  plans: PaymentPlanOption[];
  isLoading: boolean;
  error: string | null;
};

export const useLivePrices = (): LivePricesState => {
  const [state, setState] = useState<LivePricesState>({
    plans: VIREO_PLANS,
    isLoading: true,
    error: null
  });
  const fetchedRef = useRef(false);

  useEffect(() => {
    if (fetchedRef.current) {
      return;
    }
    fetchedRef.current = true;

    let cancelled = false;

    void (async () => {
      try {
        const { fetchPrices } = await import("../api/checkout");
        const prices = await fetchPrices();
        if (!cancelled) {
          setState({
            plans: mapPricesResponseToPlans(prices),
            isLoading: false,
            error: null
          });
        }
      } catch (error) {
        if (!cancelled) {
          console.warn("[useLivePrices] Failed to fetch prices, using fallback", error);
          setState({
            plans: VIREO_PLANS,
            isLoading: false,
            error: error instanceof Error ? error.message : "Failed to fetch prices"
          });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  return state;
};
